import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import PermissionGuard from "../../components/PermissionGuard";
import { roleApi, Role, Permission } from "./role-api";

const RoleManagement: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    permissions: [] as string[],
    is_active: true,
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteRoleId, setDeleteRoleId] = useState<number | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRole, setViewingRole] = useState<Role | null>(null);
  const [seedingPermissions, setSeedingPermissions] = useState(false);
  const [showSeedRolesModal, setShowSeedRolesModal] = useState(false);
  const [seedingRoles, setSeedingRoles] = useState<Record<string, boolean>>({});
  const [seedingAllRoles, setSeedingAllRoles] = useState(false);

  // Fetch permissions on mount
  useEffect(() => {
    fetchPermissions();
  }, []);

  // Fetch roles on mount and when search query changes
  useEffect(() => {
    fetchRoles();
  }, [searchQuery]);

  const fetchPermissions = async () => {
    setLoadingPermissions(true);
    try {
      const data = await roleApi.getAllPermissions();
      setPermissions(data);
    } catch (error: any) {
      toast.error(error.message || "Failed to fetch permissions");
    } finally {
      setLoadingPermissions(false);
    }
  };

  const handleSeedPermissions = async () => {
    setSeedingPermissions(true);
    try {
      await roleApi.seedPermissions();
      toast.success("Permissions seeded successfully");
      // Refresh permissions list
      await fetchPermissions();
    } catch (error: any) {
      toast.error(error.message || "Failed to seed permissions");
    } finally {
      setSeedingPermissions(false);
    }
  };

  // Default roles to seed
  const defaultRolesToSeed: Array<{
    name: string;
    description: string;
    permissions: string[];
  }> = [
    {
      name: "Admin",
      description: "Full administrative access with all permissions",
      permissions: ["admin"],
    },
    {
      name: "Member Manager",
      description: "Manage members, visitors, and family details",
      permissions: [
        "member.view",
        "member.create",
        "member.update",
        "member.delete",
        "member.manage",
        "visitor.view",
        "visitor.create",
        "visitor.update",
        "visitor.manage",
        "family.view",
        "family.create",
        "family.update",
        "family.manage",
      ],
    },
    {
      name: "Zone Manager",
      description: "Manage zones and zone-related operations",
      permissions: [
        "zone.view",
        "zone.create",
        "zone.update",
        "zone.delete",
        "zone.manage",
      ],
    },
    {
      name: "User Manager",
      description: "Manage users and user accounts",
      permissions: [
        "user.view",
        "user.create",
        "user.update",
        "user.delete",
        "user.block",
        "user.manage",
      ],
    },
    {
      name: "Role Manager",
      description: "Manage roles and permissions",
      permissions: [
        "role.view",
        "role.create",
        "role.update",
        "role.delete",
        "role.manage",
      ],
    },
    {
      name: "Vehicle Manager",
      description: "Manage vehicles and vehicle-related operations",
      permissions: [
        "vehicle.view",
        "vehicle.create",
        "vehicle.update",
        "vehicle.delete",
        "vehicle.manage",
      ],
    },
    {
      name: "Messaging Manager",
      description: "Manage messaging and communication features",
      permissions: [
        "messaging.view",
        "messaging.create",
        "messaging.send",
        "messaging.manage",
      ],
    },
    {
      name: "Viewer",
      description: "Read-only access to view data",
      permissions: [
        "dashboard.view",
        "member.view",
        "visitor.view",
        "zone.view",
        "vehicle.view",
        "messaging.view",
      ],
    },
  ];

  const handleSeedSingleRole = async (roleData: typeof defaultRolesToSeed[0]) => {
    const roleKey = roleData.name;
    setSeedingRoles((prev) => ({ ...prev, [roleKey]: true }));
    try {
      // Check if role already exists
      const existingRoles = await roleApi.getAllRoles();
      const roleExists = existingRoles.some(
        (r) => r.name.toLowerCase() === roleData.name.toLowerCase()
      );

      if (roleExists) {
        toast.warning(`Role "${roleData.name}" already exists`);
        return;
      }

      await roleApi.seedRole({
        name: roleData.name,
        description: roleData.description,
        permissions: roleData.permissions,
        is_active: true,
      });
      toast.success(`Role "${roleData.name}" seeded successfully`);
      await fetchRoles();
    } catch (error: any) {
      toast.error(error.message || `Failed to seed role "${roleData.name}"`);
    } finally {
      setSeedingRoles((prev) => ({ ...prev, [roleKey]: false }));
    }
  };

  const handleSeedAllRoles = async () => {
    setSeedingAllRoles(true);
    try {
      const existingRoles = await roleApi.getAllRoles();
      const existingRoleNames = existingRoles.map((r) => r.name.toLowerCase());

      let successCount = 0;
      let skippedCount = 0;

      for (const roleData of defaultRolesToSeed) {
        if (existingRoleNames.includes(roleData.name.toLowerCase())) {
          skippedCount++;
          continue;
        }

        try {
          await roleApi.seedRole({
            name: roleData.name,
            description: roleData.description,
            permissions: roleData.permissions,
            is_active: true,
          });
          successCount++;
        } catch (error: any) {
          console.error(`Failed to seed role ${roleData.name}:`, error);
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully seeded ${successCount} role(s)`);
      }
      if (skippedCount > 0) {
        toast.info(`${skippedCount} role(s) already exist and were skipped`);
      }
      if (successCount === 0 && skippedCount === 0) {
        toast.warning("No roles were seeded");
      }

      await fetchRoles();
      setShowSeedRolesModal(false);
    } catch (error: any) {
      toast.error(error.message || "Failed to seed roles");
    } finally {
      setSeedingAllRoles(false);
    }
  };

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const data = await roleApi.getAllRoles(searchQuery);
      setRoles(data);
    } catch (error: any) {
      toast.error(error.message || "Failed to fetch roles");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    setEditingRole(null);
    setFormData({
      name: "",
      description: "",
      permissions: [],
      is_active: true,
    });
    // Ensure permissions are loaded before showing the form
    if (permissions.length === 0 && !loadingPermissions) {
      await fetchPermissions();
    }
    setShowForm(true);
  };

  const handleEdit = async (role: Role) => {
    setEditingRole(role);
    setFormData({
      name: role.name,
      description: role.description || "",
      permissions: role.permissions || [],
      is_active: role.is_active,
    });
    // Ensure permissions are loaded before showing the form
    if (permissions.length === 0 && !loadingPermissions) {
      await fetchPermissions();
    }
    setShowForm(true);
  };

  const handleDelete = (id: number) => {
    setDeleteRoleId(id);
    setShowDeleteModal(true);
  };

  const handleView = (role: Role) => {
    setViewingRole(role);
    setShowViewModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteRoleId) return;

    try {
      await roleApi.deleteRole(deleteRoleId);
      toast.success("Role deleted successfully");
      fetchRoles();
      setShowDeleteModal(false);
      setDeleteRoleId(null);
    } catch (error: any) {
      toast.error(error.message || "Failed to delete role");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingRole) {
        await roleApi.updateRole(editingRole.id, formData);
        toast.success("Role updated successfully");
      } else {
        await roleApi.createRole(formData);
        toast.success("Role created successfully");
      }
      setShowForm(false);
      fetchRoles();
    } catch (error: any) {
      toast.error(error.message || "Failed to save role");
    }
  };

  const togglePermission = (permission: string) => {
    setFormData((prev) => {
      const currentPermissions = prev.permissions || [];
      const isManagePermission = permission.endsWith('.manage');
      
      // Find the category this permission belongs to
      const permissionObj = permissions.find(p => p.name === permission);
      const category = permissionObj?.category || "other";
      const categoryPermissions = permissionsByCategory[category] || [];
      
      if (isManagePermission) {
        // If clicking .manage permission
        if (currentPermissions.includes(permission)) {
          // Unchecking .manage: remove all permissions in this category
          const categoryPermNames = categoryPermissions.map(p => p.name);
          return {
            ...prev,
            permissions: currentPermissions.filter((p) => !categoryPermNames.includes(p)),
          };
        } else {
          // Checking .manage: add all permissions in this category
          const categoryPermNames = categoryPermissions.map(p => p.name);
          const newPermissions = [...currentPermissions];
          categoryPermNames.forEach(perm => {
            if (!newPermissions.includes(perm)) {
              newPermissions.push(perm);
            }
          });
          return {
            ...prev,
            permissions: newPermissions,
          };
        }
      } else {
        // If clicking a regular permission
        const basePermission = permission.split('.')[0];
        const managePermission = `${basePermission}.manage`;
        const managePermissionObj = categoryPermissions.find(p => p.name === managePermission);
        
        if (currentPermissions.includes(permission)) {
          // Unchecking: remove this permission and .manage if it exists
          const newPermissions = currentPermissions.filter((p) => p !== permission);
          if (managePermissionObj && newPermissions.includes(managePermission)) {
            newPermissions.splice(newPermissions.indexOf(managePermission), 1);
          }
          return {
            ...prev,
            permissions: newPermissions,
          };
        } else {
          // Checking: add this permission
          const newPermissions = [...currentPermissions, permission];
          
          // Check if all permissions in category are now selected (except .manage)
          const categoryPermNames = categoryPermissions.map(p => p.name);
          const selectedInCategory = newPermissions.filter(p => categoryPermNames.includes(p));
          const allSelected = categoryPermNames.every(perm => 
            perm === managePermission || selectedInCategory.includes(perm)
          );
          
          // If all permissions are selected, also add .manage
          if (allSelected && managePermissionObj && !newPermissions.includes(managePermission)) {
            newPermissions.push(managePermission);
          }
          
          return {
            ...prev,
            permissions: newPermissions,
          };
        }
      }
    });
  };

  const addCustomPermission = () => {
    const permission = prompt("Enter custom permission name:");
    if (permission && permission.trim()) {
      setFormData((prev) => ({
        ...prev,
        permissions: [...(prev.permissions || []), permission.trim()],
      }));
    }
  };

  const removeCustomPermission = (permission: string) => {
    setFormData((prev) => ({
      ...prev,
      permissions: (prev.permissions || []).filter((p) => p !== permission),
    }));
  };

  // Group permissions by category and sort so .manage permissions come first
  const permissionsByCategory = permissions.reduce((acc, perm) => {
    const category = perm.category || "other";
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(perm);
    return acc;
  }, {} as Record<string, Permission[]>);

  // Sort permissions in each category: .manage first, then others
  Object.keys(permissionsByCategory).forEach((category) => {
    permissionsByCategory[category].sort((a, b) => {
      const aIsManage = a.name.endsWith('.manage');
      const bIsManage = b.name.endsWith('.manage');
      if (aIsManage && !bIsManage) return -1;
      if (!aIsManage && bIsManage) return 1;
      return a.name.localeCompare(b.name);
    });
  });

  const permissionNames = permissions.map(p => p.name);
  const customPermissions = formData.permissions.filter(
    (p) => !permissionNames.includes(p)
  );

  // Check if user is Super Admin
  const role = Number(localStorage.getItem("role"));
  const isSuperAdmin = localStorage.getItem("isSuperAdmin") === "true" || role === 0;

  // Hide seed buttons completely once there's any data in the tables
  // Show Seed Permissions button ONLY if:
  // - Permissions have finished loading (!loadingPermissions)
  // - Permissions table is empty (permissions.length === 0)
  // - AND user is Super Admin
  const shouldShowSeedPermissions = !loadingPermissions && permissions.length === 0 && isSuperAdmin;

  // Show Seed Roles button ONLY if:
  // - Roles have finished loading (!loading)
  // - Roles table is empty (roles is array and length === 0)
  // - AND user is Super Admin
  const shouldShowSeedRoles = !loading && Array.isArray(roles) && roles.length === 0 && isSuperAdmin;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Role Management</h1>
        <div className="flex items-center gap-3">
          {/* Seed Permissions Button - Only show if permissions table is empty AND user is Super Admin */}
          {shouldShowSeedPermissions && (
            <button
              onClick={handleSeedPermissions}
              disabled={seedingPermissions}
              className="bg-black px-4 py-2 rounded hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {seedingPermissions ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                  <span className="text-white">Seeding...</span>
                </>
              ) : (
                <>
                  <i className="fa-solid fa-seedling text-white"></i>
                  <span className="text-white">Seed Permissions</span>
                </>
              )}
            </button>
          )}
          {/* Seed Roles Button - Only show if roles table is empty AND user is Super Admin */}
          {shouldShowSeedRoles && (
            <PermissionGuard permission={["role.create", "role.manage"]}>
              <button
                onClick={() => setShowSeedRolesModal(true)}
                className="bg-black px-4 py-2 rounded hover:bg-gray-800 flex items-center gap-2"
              >
                <i className="fa-solid fa-users-gear text-white"></i>
                <span className="text-white">Seed Roles</span>
              </button>
            </PermissionGuard>
          )}
          <PermissionGuard permission={["role.create", "role.manage"]}>
            <button
              onClick={handleCreate}
              className="bg-black px-4 py-2 rounded hover:bg-gray-800 flex items-center gap-2"
            >
              <i className="fa-solid fa-plus text-white"></i>
              <span className="text-white">Create Role</span>
            </button>
          </PermissionGuard>
        </div>
      </div>

      {/* Search */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search roles..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full max-w-md px-4 py-2 border border-gray-300 rounded"
        />
      </div>

      {/* Roles List */}
      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Permissions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {roles.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                    No roles found
                  </td>
                </tr>
              ) : (
                roles.map((role) => (
                  <tr key={role.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{role.name}</div>
                      <div className="text-xs text-gray-400 mt-1">{role.slug}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {role.description || "-"}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      <div className="flex flex-wrap gap-1">
                        {role.permissions?.slice(0, 3).map((perm) => (
                          <span
                            key={perm}
                            className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs"
                          >
                            {perm}
                          </span>
                        ))}
                        {role.permissions && role.permissions.length > 3 && (
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                            +{role.permissions.length - 3} more
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 rounded text-xs ${
                          role.is_active
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {role.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleView(role)}
                          className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                          title="View role details"
                        >
                          <i className="fa-solid fa-eye"></i>
                        </button>
                        <PermissionGuard permission={["role.update", "role.manage"]}>
                          <button
                            onClick={() => handleEdit(role)}
                            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 p-1.5 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                            title="Edit role"
                          >
                            <i className="fa-solid fa-edit"></i>
                          </button>
                        </PermissionGuard>
                        <PermissionGuard permission={["role.delete", "role.manage"]}>
                          <button
                            onClick={() => handleDelete(role.id)}
                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                            title="Delete role"
                          >
                            <i className="fa-solid fa-trash"></i>
                          </button>
                        </PermissionGuard>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Form Modal */}
      {showForm && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowForm(false)}
        >
          <div 
            className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold mb-4">
              {editingRole ? "Edit Role" : "Create Role"}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Permissions
                </label>
                {loadingPermissions ? (
                  <div className="text-center py-4">Loading permissions...</div>
                ) : permissions.length === 0 ? (
                  <div className="text-center py-4 text-gray-500">
                    No permissions available. Please seed permissions first.
                  </div>
                ) : (
                  <div className="border border-gray-300 rounded p-4 max-h-96 overflow-y-auto">
                    {Object.entries(permissionsByCategory).map(([category, perms]) => (
                      <div key={category} className="mb-4 last:mb-0">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2 capitalize">
                          {category}
                        </h4>
                        <div className="grid grid-cols-2 gap-2">
                          {perms.map((perm) => {
                            const isManage = perm.name.endsWith('.manage');
                            return (
                              <label 
                                key={perm.id} 
                                className={`flex items-center ${isManage ? 'font-semibold' : ''}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={formData.permissions.includes(perm.name)}
                                  onChange={() => togglePermission(perm.name)}
                                  className="mr-2"
                                />
                                <span className={`text-sm ${isManage ? 'text-blue-600 dark:text-blue-400' : ''}`}>
                                  {perm.name}
                                  {isManage && (
                                    <span className="text-xs text-blue-500 dark:text-blue-400 ml-1 font-normal">
                                      (All {category} permissions)
                                    </span>
                                  )}
                                  {!isManage && perm.description && (
                                    <span className="text-xs text-gray-500 ml-1">
                                      ({perm.description})
                                    </span>
                                  )}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                    {customPermissions.length > 0 && (
                      <div className="mt-4 pt-4 border-t">
                        <p className="text-sm font-medium mb-2">Custom Permissions:</p>
                        <div className="flex flex-wrap gap-2">
                          {customPermissions.map((perm) => (
                            <span
                              key={perm}
                              className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs flex items-center"
                            >
                              {perm}
                              <button
                                type="button"
                                onClick={() => removeCustomPermission(perm)}
                                className="ml-2 text-purple-600 hover:text-purple-900"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={addCustomPermission}
                      className="mt-2 text-sm text-blue-600 hover:text-blue-800"
                    >
                      + Add Custom Permission
                    </button>
                  </div>
                )}
              </div>

              <div className="mb-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) =>
                      setFormData({ ...formData, is_active: e.target.checked })
                    }
                    className="mr-2"
                  />
                  <span className="text-sm">Active</span>
                </label>
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-black rounded hover:bg-gray-800 flex items-center gap-2"
                >
                  {editingRole ? (
                    <>
                      <i className="fa-solid fa-save text-white"></i>
                      <span className="text-white">Update Role</span>
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-plus text-white"></i>
                      <span className="text-white">Create Role</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Role Modal */}
      {showViewModal && viewingRole && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Role Details
              </h2>
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setViewingRole(null);
                }}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                aria-label="Close"
              >
                <i className="fa-solid fa-times text-xl"></i>
              </button>
            </div>

            <div className="space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Basic Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      Name
                    </label>
                    <p className="text-base text-gray-900 dark:text-white font-medium">
                      {viewingRole.name}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      Slug
                    </label>
                    <p className="text-base text-gray-900 dark:text-white">
                      {viewingRole.slug}
                    </p>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      Description
                    </label>
                    <p className="text-base text-gray-900 dark:text-white">
                      {viewingRole.description || "No description"}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      Status
                    </label>
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                        viewingRole.is_active
                          ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                          : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"
                      }`}
                    >
                      {viewingRole.is_active ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Permissions */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Permissions ({viewingRole.permissions?.length || 0})
                </h3>
                {viewingRole.permissions && viewingRole.permissions.length > 0 ? (
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-900/50">
                    {Object.entries(
                      viewingRole.permissions.reduce((acc, perm) => {
                        const category = permissions.find(p => p.name === perm)?.category || "other";
                        if (!acc[category]) {
                          acc[category] = [];
                        }
                        acc[category].push(perm);
                        return acc;
                      }, {} as Record<string, string[]>)
                    ).map(([category, perms]) => (
                      <div key={category} className="mb-4 last:mb-0">
                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 capitalize">
                          {category} ({perms.length})
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {perms
                            .sort((a, b) => {
                              const aIsManage = a.endsWith('.manage');
                              const bIsManage = b.endsWith('.manage');
                              if (aIsManage && !bIsManage) return -1;
                              if (!aIsManage && bIsManage) return 1;
                              return a.localeCompare(b);
                            })
                            .map((perm) => {
                              const isManage = perm.endsWith('.manage');
                              return (
                                <span
                                  key={perm}
                                  className={`px-3 py-1 rounded text-sm ${
                                    isManage
                                      ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 font-semibold"
                                      : "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                                  }`}
                                >
                                  {perm}
                                </span>
                              );
                            })}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400">No permissions assigned</p>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setViewingRole(null);
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Delete Role</h2>
            <p className="mb-6">
              Are you sure you want to delete this role? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteRoleId(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-black rounded hover:bg-gray-800"
              >
                <span className="text-white">Delete</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Seed Roles Modal */}
      {showSeedRolesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-6 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Seed Roles
              </h2>
              <button
                onClick={() => setShowSeedRolesModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                aria-label="Close"
              >
                <i className="fa-solid fa-times text-xl"></i>
              </button>
            </div>

            <div className="p-6">
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                The following roles will be created with their associated permissions. Roles that already exist will be skipped.
              </p>

              <div className="space-y-4 mb-6">
                {defaultRolesToSeed.map((roleData, index) => {
                  const roleKey = roleData.name;
                  const isSeeding = seedingRoles[roleKey] || false;
                  const existingRole = roles.find(
                    (r) => r.name.toLowerCase() === roleData.name.toLowerCase()
                  );

                  return (
                    <div
                      key={index}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-900/50"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                            {roleData.name}
                            {existingRole && (
                              <span className="ml-2 text-xs px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 rounded">
                                Already Exists
                              </span>
                            )}
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {roleData.description}
                          </p>
                        </div>
                        <button
                          onClick={() => handleSeedSingleRole(roleData)}
                          disabled={isSeeding || !!existingRole}
                          className={`px-4 py-2 rounded text-sm font-medium flex items-center gap-2 ${
                            existingRole
                              ? "bg-gray-300 dark:bg-gray-700 cursor-not-allowed"
                              : "bg-black hover:bg-gray-800"
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          {isSeeding ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                              <span className={existingRole ? "text-gray-500 dark:text-gray-400" : "text-white"}>Seeding...</span>
                            </>
                          ) : (
                            <>
                              <i className={`fa-solid fa-seedling ${existingRole ? "text-gray-500 dark:text-gray-400" : "text-white"}`}></i>
                              <span className={existingRole ? "text-gray-500 dark:text-gray-400" : "text-white"}>Seed</span>
                            </>
                          )}
                        </button>
                      </div>

                      <div className="mt-3">
                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                          Permissions ({roleData.permissions.length})
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {roleData.permissions.map((perm, permIndex) => {
                            const isManage = perm.endsWith(".manage");
                            const isWildcard = perm === "*";
                            return (
                              <span
                                key={permIndex}
                                className={`px-2 py-1 rounded text-xs ${
                                  isWildcard
                                    ? "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 font-bold"
                                    : isManage
                                    ? "bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 font-semibold"
                                    : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                                }`}
                              >
                                {perm}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setShowSeedRolesModal(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSeedAllRoles}
                  disabled={seedingAllRoles}
                  className="px-6 py-2 bg-black rounded hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {seedingAllRoles ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                      <span className="text-white">Seeding All Roles...</span>
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-seedling text-white"></i>
                      <span className="text-white">Seed All Roles</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleManagement;
